import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Instagram, MapPin, Clock, Star, Scissors, Award } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Logo } from "@/components/Logo";
import { BookingWizard } from "@/components/BookingWizard";
import { publicApi } from "@/lib/api";
import { priceLabel } from "@/lib/format";

const HERO_BG = "https://images.unsplash.com/photo-1773904215697-e6c21fc27ac2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const ABOUT_IMG = "https://images.unsplash.com/photo-1599011176306-4a96f1516d4d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Landing() {
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    publicApi.barbers().then(setBarbers).catch(() => {});
    publicApi.services().then(setServices).catch(() => {});
    publicApi.settings().then(setSettings).catch(() => {});
  }, []);

  const rate = settings?.exchange_rate || 89000;
  const contact = settings?.contact || {};

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="relative min-h-screen bg-obsidian-900 text-white">
      <div className="grain" />
      <Navbar />

      {/* HERO */}
      <section id="home" className="relative flex min-h-screen items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Ali Malak Barbershop" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900 via-obsidian-900/85 to-obsidian-900/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-obsidian-900 via-transparent to-obsidian-900/60" />
        </div>

        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-24 lg:px-8">
          <motion.div initial="hidden" animate="show" variants={fadeIn} className="max-w-2xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-obsidian-900/50 px-4 py-1.5 backdrop-blur">
              <Star size={13} className="text-gold" fill="currentColor" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-gold">Premium Barbershop · Lebanon</span>
            </div>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl">
              Precision Cuts.<br />
              <span className="gold-text">Timeless Style.</span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-gray-300 sm:text-lg">
              At Ali Malak Barbershop, master craftsmanship meets modern luxury. Custom fades, hot-towel shaves,
              and beard sculpting — crafted for the modern gentleman.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <button
                data-testid="hero-book-btn"
                onClick={() => scrollTo("#booking")}
                className="rounded-full bg-gold px-8 py-4 text-base font-bold text-obsidian-900 transition-colors duration-200 hover:bg-gold-hover"
              >
                Book Appointment
              </button>
              <button
                data-testid="hero-services-btn"
                onClick={() => scrollTo("#services")}
                className="rounded-full border border-white/20 px-8 py-4 text-base font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold"
              >
                View Services & Rates
              </button>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-gray-400">
              <span className="flex items-center gap-2"><Award size={16} className="text-gold" /> 3 Master Barbers</span>
              <span className="flex items-center gap-2"><Clock size={16} className="text-gold" /> Tue–Sun · 10AM–12AM</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <Section id="about" className="py-20 lg:py-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeIn}>
            <Eyebrow>The Legacy</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">
              The Art of the <span className="gold-text">Ali Malak</span> Cut
            </h2>
            <p className="mt-6 text-base leading-relaxed text-gray-300">
              Founded in the heart of Lebanon, Ali Malak Barbershop blends centuries-old Mediterranean grooming
              traditions with modern precision barbering. Every cut is an art form — tailored to your individual
              style, face structure and personality.
            </p>
            <p className="mt-4 text-base leading-relaxed text-gray-400">
              Step into a space built for the modern gentleman: warm ambience, sharp detail, and a team that treats
              your look as a craft, not a routine.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4">
              {[["12+", "Years Mastery"], ["5★", "Client Rating"], ["3", "Expert Barbers"]].map(([n, l]) => (
                <div key={l} className="rounded-2xl border border-white/10 bg-obsidian-card p-4 text-center">
                  <div className="font-display text-2xl font-bold text-gold">{n}</div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-gray-400">{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeIn} className="relative">
            <div className="overflow-hidden rounded-3xl border border-gold/20">
              <img src={ABOUT_IMG} alt="Barber at work" className="h-[460px] w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-2xl border border-gold/30 bg-obsidian-800 p-5 shadow-2xl sm:block">
              <Scissors className="text-gold" size={28} />
              <div className="mt-2 font-display text-lg font-bold">Master Craft</div>
              <div className="text-xs text-gray-400">Every detail matters</div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* BARBERS */}
      <Section id="barbers" className="py-20 lg:py-28">
        <div className="text-center">
          <Eyebrow>Our Team</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">Meet the Master Barbers</h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {barbers.map((b, i) => (
            <motion.div
              key={b.id}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: i * 0.1 }}
              className="group overflow-hidden rounded-3xl border border-white/10 bg-obsidian-card transition-colors duration-300 hover:border-gold/50"
              data-testid={`barber-card-${b.id}`}
            >
              <div className="relative h-72 overflow-hidden">
                <img src={b.image_url} alt={b.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian-card via-transparent to-transparent" />
                <span className="absolute right-3 top-3 rounded-full border border-gold/40 bg-obsidian-900/70 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-gold">
                  {b.experience}
                </span>
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl font-bold">{b.name}</h3>
                <div className="mt-1 font-mono text-xs uppercase tracking-wider text-gold">{b.role}</div>
                <p className="mt-3 text-sm text-gray-400">{b.specialty}</p>
                <button
                  onClick={() => scrollTo("#booking")}
                  data-testid={`barber-book-${b.id}`}
                  className="mt-5 w-full rounded-full border border-gold/40 py-2.5 text-sm font-semibold text-gold transition-colors duration-200 hover:bg-gold-soft"
                >
                  Book with {b.name.split(" ")[0]}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SERVICES */}
      <Section id="services" className="py-20 lg:py-28">
        <div className="text-center">
          <Eyebrow>Services & Pricing</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">Grooming, Perfected</h2>
          <p className="mx-auto mt-4 max-w-md text-sm text-gray-400">Prices shown in USD and Lebanese Pound. Every appointment is one hour.</p>
        </div>
        <div className="mx-auto mt-14 max-w-3xl divide-y divide-white/10 rounded-3xl border border-white/10 bg-obsidian-card">
          {services.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-4 p-5 sm:p-6" data-testid={`service-row-${s.id}`}>
              <div className="flex items-start gap-4">
                <Scissors className="mt-1 shrink-0 text-gold" size={18} />
                <div>
                  <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                  <p className="text-sm text-gray-400">{s.description}</p>
                </div>
              </div>
              <div className="whitespace-nowrap text-right">
                <div className="font-mono text-base font-bold text-gold">${s.price_usd}</div>
                <div className="font-mono text-[10px] text-gray-500">{(s.price_usd * rate).toLocaleString()} L.L.</div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* BOOKING */}
      <Section id="booking" className="py-20 lg:py-28">
        <div className="text-center">
          <Eyebrow>Reserve Your Seat</Eyebrow>
          <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">Book in 60 Seconds</h2>
          <p className="mx-auto mt-4 max-w-md text-sm text-gray-400">Choose your barber, service and time. We'll take care of the rest.</p>
        </div>
        <div className="mt-14">
          {settings && barbers.length > 0 && services.length > 0 ? (
            <BookingWizard barbers={barbers} services={services} settings={settings} />
          ) : (
            <div className="text-center text-gray-500">Loading booking…</div>
          )}
        </div>
      </Section>

      {/* CONTACT */}
      <Section id="contact" className="py-20 lg:py-28">
        <div className="grid gap-10 lg:grid-cols-2">
          <div>
            <Eyebrow>Visit Us</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-bold sm:text-4xl lg:text-5xl">Come Through</h2>
            <p className="mt-4 text-sm text-gray-400">{contact.address}</p>
            <div className="mt-8 space-y-3">
              <ContactRow icon={Phone} label="Phone" value={contact.phone} href={`tel:${contact.phone}`} testid="contact-phone" />
              <ContactRow icon={MessageCircle} label="WhatsApp" value={`+${contact.whatsapp}`} href={`https://wa.me/${contact.whatsapp}`} testid="contact-whatsapp" />
              <ContactRow icon={Instagram} label="Instagram" value={`@${contact.instagram}`} href={`https://instagram.com/${contact.instagram}`} testid="contact-instagram" />
              <ContactRow icon={MapPin} label="Location" value="Open in Google Maps" href={`https://www.google.com/maps?q=${encodeURIComponent(contact.address || "Beirut, Lebanon")}`} testid="contact-map" />
            </div>
            <div className="mt-8 rounded-2xl border border-gold/20 bg-obsidian-card p-5">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gold"><Clock size={14} /> Working Hours</div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-gray-300">Tuesday – Sunday</span><span className="font-mono text-gold">10:00 AM – 12:00 AM</span></div>
                <div className="flex justify-between"><span className="text-gray-300">Monday</span><span className="font-mono text-gray-500">Closed</span></div>
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-white/10">
            <iframe
              title="map"
              src={contact.map_embed || "https://www.google.com/maps?q=Beirut,Lebanon&output=embed"}
              className="h-full min-h-[400px] w-full grayscale"
              loading="lazy"
            />
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 bg-obsidian-800 py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-6 px-5 text-center lg:flex-row lg:justify-between lg:text-left">
          <Logo />
          <p className="text-xs text-gray-500">© {new Date().getFullYear()} Ali Malak Barbershop · Lebanon. Crafted for the modern gentleman.</p>
          <div className="flex gap-4">
            <a href={`https://wa.me/${contact.whatsapp}`} className="text-gray-400 hover:text-gold"><MessageCircle size={20} /></a>
            <a href={`https://instagram.com/${contact.instagram}`} className="text-gray-400 hover:text-gold"><Instagram size={20} /></a>
            <a href={`tel:${contact.phone}`} className="text-gray-400 hover:text-gold"><Phone size={20} /></a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Section = ({ id, className = "", children }) => (
  <section id={id} className={className}>
    <div className="mx-auto max-w-7xl px-5 lg:px-8">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{children}</span>
);

const ContactRow = ({ icon: Icon, label, value, href, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="flex items-center gap-4 rounded-2xl border border-white/10 bg-obsidian-card p-4 transition-colors duration-200 hover:border-gold/50"
  >
    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-gold/30 bg-gold-soft text-gold">
      <Icon size={18} />
    </div>
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="font-medium text-white">{value}</div>
    </div>
  </a>
);
